import * as Account from '../account'
import * as AccountGroup from '../account-group'
import { User } from './user'

export namespace UserSettings {
    /**
     * @name AlertPlatform
     * @description slack or email
     */
    export enum AlertPlatform {
        SLACK = 'slack',
        EMAIL = 'email',
    }

    /**
     * @name AlertSetting
     * @description alert settings for an alert platform
     */
    export interface AlertSetting {
        platform: AlertPlatform
        delta: boolean
        budget: boolean
        improvements: boolean
        conversionRecord: boolean
        conversionActionsFlatline: boolean
        impressionsFlatline: boolean
    }

    /**
     * @name AlertSettingsResponse
     * @description alert settings for all platforms
     */
    export interface AlertSettingsResponse {
        alertSettings: AlertSetting[]
        slackChannel: string | null
        slackOAuthUrl: string | null
    }

    export type AlertType = keyof Omit<UserSettings.AlertSetting, 'platform'>

    export interface AccountsSettings {
        /**
         * Account metric type to show in the first column
         */
        columnOneMetric: Account.MetricType
        /**
         * Account metric type to show in the second column
         */
        columnTwoMetric: Account.MetricType
        /**
         * Show the account group selector on the account row
         */
        showAccountGroupColumn: boolean
        /**
         * Show the account manager selector on the account row
         */
        showAccountManagerColumn: boolean
        /**
         * ID of the account group that is pinned to the top of the account list.
         * If null, no account group is pinned.
         */
        pinnedAccountGroupId: AccountGroup.ID | null
        /**
         * ID of the manager that is pinned to the top of the account manager list.
         * If null, no manager is pinned.
         */
        pinnedManagerUserId: User.ID | null
    }

    export interface GetAlertSettingsResponse {
        alertSettings: AlertSetting[]
        slackChannel: string | null
        slackOAuthUrl: string | null
    }

    export interface AddSlackIntergrationRequest {
        userId: User.ID
        code: string
    }

    export interface AddSlackIntergrationResponse {
        redirectUrl: string
    }

    export interface ToggleAlertSettingRequest {
        userId: User.ID
        alterSetting: AlertSetting
    }

    export interface RemoveTeamMemberRequest {
        userId: User.ID
        removalUserId: User.ID
    }

    export interface AddTeamMemberRequest {
        userId: User.ID
        name: string
        emailAddress: string
    }

    export interface SlackAlertSettings {
        slack: {
            budget: boolean
            conversion: boolean
            conversionActionsFlatline: boolean
            impressionsFlatline: boolean
            improvement: boolean
            kpi: boolean
            scheduled_report?: boolean
            over_budget: boolean
            smart_bidding_experiment: boolean
        }
        muted_accounts: Account.ID[]
    }

    export interface EmailAlertSettings {
        email: {
            budget: boolean
            conversion: boolean
            conversionActionsFlatline: boolean
            impressionsFlatline: boolean
            improvement: boolean
            kpi: boolean
            scheduled_report?: boolean
            over_budget: boolean
            smart_bidding_experiment: boolean
        }
    }
}
